import React, { FC, useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { LuRefreshCw, LuSparkles, LuX } from "react-icons/lu";

import {
  AiWorkSummaryResponse,
  useAiWorkSummaryMutation,
} from "../../slices/AiSlice";
import { aiErrorMessage, parseSummary, timeAgo } from "./ai.utils";
import { useAiStatus } from "./useAiStatus";

type Props = { workId: string };

const SECTION_COLORS: { match: RegExp; color: string }[] = [
  { match: /done|completed/i, color: "#22A06B" },
  { match: /still|to do|next/i, color: "#6B4EFF" },
  { match: /waiting|block/i, color: "#E5484D" },
  { match: /contact|client/i, color: "#F5A524" },
];
const sectionColor = (title: string) =>
  SECTION_COLORS.find((item) => item.match.test(title))?.color || "#8A90A2";

const Skeleton: FC = () => (
  <div
    role={"status"}
    aria-label={"Loading summary"}
    className={"animate-pulse"}>
    <div className={"h-3 w-24 rounded bg-[#ECEEF3]"} />
    <div className={"mt-2.5 h-4 w-full rounded bg-[#ECEEF3]"} />
    <div className={"mt-2 h-4 w-4/5 rounded bg-[#ECEEF3]"} />
    {[0, 1, 2].map((index) => (
      <div key={index} className={"mt-6"}>
        <div className={"h-3.5 w-32 rounded bg-[#ECEEF3]"} />
        <div className={"mt-2.5 h-3 w-11/12 rounded bg-[#F2F3F7]"} />
        <div className={"mt-2 h-3 w-3/4 rounded bg-[#F2F3F7]"} />
      </div>
    ))}
  </div>
);

/** The summary itself (also used by the preview). */
export const CatchMeUpSummary: FC<{ summary: string }> = ({ summary }) => {
  const { lead, sections } = parseSummary(summary);
  return (
    <div>
      {lead && (
        <div
          className={
            "rounded-[12px] border border-[#E4DEFF] bg-[#F7F5FF] px-4 py-3"
          }>
          <div
            className={
              "text-[11px] font-bold uppercase tracking-[0.05em] text-[#6B4EFF]"
            }>
            Where it stands
          </div>
          <div
            className={
              "mt-1 text-[14.5px] font-semibold leading-[1.5] text-[#1F2433]"
            }>
            {lead}
          </div>
        </div>
      )}
      {sections.map((section, index) => (
        <div key={`${section.title}-${index}`} className={"mt-4"}>
          {section.title && (
            <div
              className={
                "mb-1.5 flex items-center gap-2 text-[13.5px] font-extrabold text-[#1F2433]"
              }>
              <span
                className={"h-2 w-2 rounded-full"}
                style={{ background: sectionColor(section.title) }}
              />
              {section.title}
            </div>
          )}
          {section.bullets.length ? (
            <ul className={"space-y-1 pl-4"}>
              {section.bullets.map((bullet, bulletIndex) => (
                <li
                  key={bulletIndex}
                  className={
                    "list-disc text-[13.5px] leading-[1.5] text-[#3A4256] marker:text-[#B4BAC8]"
                  }>
                  {bullet}
                </li>
              ))}
            </ul>
          ) : (
            <div className={"pl-4 text-[13px] text-[#8A90A2]"}>Nothing yet</div>
          )}
        </div>
      ))}
    </div>
  );
};

/**
 * "Catch me up" on the work item Summary: an AI summary of where the work
 * stands. Hidden unless the firm has AI switched on.
 */
export const CatchMeUp: FC<Props> = ({ workId }) => {
  const { isOn } = useAiStatus();
  const [getSummary, { isLoading }] = useAiWorkSummaryMutation();
  const [isOpen, setIsOpen] = useState(false);
  const [result, setResult] = useState<AiWorkSummaryResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [, setTick] = useState(0);

  // Keep "Generated … ago" fresh while open.
  useEffect(() => {
    if (!isOpen) return;
    const timer = window.setInterval(() => setTick((tick) => tick + 1), 30000);
    return () => window.clearInterval(timer);
  }, [isOpen]);

  // A different work item: forget the old summary.
  useEffect(() => {
    setResult(null);
    setError(null);
  }, [workId]);

  if (!isOn || !workId) return null;

  const load = async () => {
    setError(null);
    try {
      setResult(await getSummary({ workId }).unwrap());
    } catch (caught) {
      setError(aiErrorMessage(caught));
    }
  };

  const open = () => {
    setIsOpen(true);
    if (!result && !isLoading) void load();
  };

  return (
    <>
      <div
        className={
          "mb-4 flex items-center gap-3 rounded-[12px] border border-[#E4DEFF] bg-gradient-to-r from-[#F7F5FF] to-white px-4 py-3 font-Manrope"
        }>
        <span
          className={
            "flex h-9 w-9 shrink-0 items-center justify-center rounded-[10px] bg-[#6B4EFF] text-white"
          }>
          <LuSparkles size={17} />
        </span>
        <div className={"min-w-0 flex-1"}>
          <div className={"text-[14px] font-extrabold text-[#1F2433]"}>
            New here or back after a while?
          </div>
          <div className={"truncate text-[12.5px] text-[#5B6678]"}>
            Get a quick AI summary of where this work stands.
          </div>
        </div>
        <button
          type={"button"}
          onClick={open}
          className={
            "inline-flex h-9 shrink-0 items-center gap-1.5 rounded-lg bg-[#6B4EFF] px-3.5 text-[13px] font-bold text-white shadow-[0_6px_16px_rgba(107,78,255,0.25)] hover:bg-[#5A3EEB]"
          }>
          <LuSparkles size={14} />
          Catch me up
        </button>
      </div>

      {isOpen &&
        createPortal(
          <div
            className={
              "fixed inset-0 z-[75] flex items-center justify-center bg-[rgba(20,22,34,0.45)] p-4 font-Manrope"
            }
            onClick={() => setIsOpen(false)}>
            <div
              role={"dialog"}
              aria-modal={"true"}
              aria-label={"Catch me up"}
              onClick={(event) => event.stopPropagation()}
              className={
                "flex max-h-[calc(100vh-48px)] w-[580px] max-w-full flex-col overflow-hidden rounded-[16px] bg-white shadow-[0_24px_70px_rgba(0,0,0,0.28)]"
              }>
              <div
                className={
                  "flex items-center gap-2.5 border-b border-[#EDEFF3] px-5 py-4"
                }>
                <span
                  className={
                    "flex h-7 w-7 items-center justify-center rounded-lg bg-[#F0EDFF] text-[#6B4EFF]"
                  }>
                  <LuSparkles size={15} />
                </span>
                <span
                  className={
                    "flex-1 text-[16px] font-extrabold text-[#1F2433]"
                  }>
                  Catch me up
                </span>
                <button
                  type={"button"}
                  aria-label={"Close"}
                  onClick={() => setIsOpen(false)}
                  className={
                    "flex h-8 w-8 items-center justify-center rounded-lg text-[#8A90A2] hover:bg-[#F4F5F8]"
                  }>
                  <LuX size={16} />
                </button>
              </div>

              <div className={"min-h-[200px] flex-1 overflow-y-auto px-5 py-4"}>
                {isLoading ? (
                  <Skeleton />
                ) : error ? (
                  <div
                    role={"alert"}
                    className={
                      "rounded-lg border border-[#F6C9CB] bg-[#FDF1F1] px-3.5 py-3 text-[13px] text-[#B4262C]"
                    }>
                    {error}
                  </div>
                ) : result ? (
                  <CatchMeUpSummary summary={result.summary} />
                ) : null}
              </div>

              <div className={"border-t border-[#EDEFF3] px-5 py-3"}>
                <div className={"flex items-center gap-3"}>
                  <span className={"flex-1 text-[12px] text-[#5B6678]"}>
                    {isLoading
                      ? "Reading this work item…"
                      : result
                      ? `Generated ${timeAgo(result.generatedAt)}`
                      : ""}
                  </span>
                  <button
                    type={"button"}
                    disabled={isLoading}
                    onClick={() => void load()}
                    className={
                      "inline-flex h-8 items-center gap-1.5 rounded-lg border border-[#DCE2EA] px-3 text-[12.5px] font-semibold text-[#3A4256] hover:bg-[#F7F8FA] disabled:opacity-50"
                    }>
                    <LuRefreshCw
                      size={13}
                      className={isLoading ? "animate-spin" : undefined}
                    />
                    Refresh
                  </button>
                </div>
                <div className={"mt-2 text-[11px] text-[#8A90A2]"}>
                  AI summary from this work item&apos;s details, subtasks, notes
                  and activity. It can make mistakes.
                </div>
              </div>
            </div>
          </div>,
          document.body,
        )}
    </>
  );
};
